/**
 * Debug Overlay for Layout System
 * 
 * Renders bounding boxes for screen bounds, safe-area insets, grid, and HUD elements.
 * Toggleable with hotkey (D key) or programmatically.
 */

import Phaser from 'phaser'
import { ComputedLayout } from './layout'

export class DebugOverlay {
  private scene: Phaser.Scene
  private graphics: Phaser.GameObjects.Graphics
  private textLabels: Phaser.GameObjects.Text[] = []
  private isVisible: boolean = false

  constructor(scene: Phaser.Scene) {
    this.scene = scene
    this.graphics = this.scene.add.graphics()
    this.graphics.setDepth(10000) // Ensure it's above everything
  }

  /**
   * Update the debug overlay with current layout
   */
  public update(layout: ComputedLayout): void {
    if (!this.isVisible) {
      return
    }

    this.clear()
    this.draw(layout)
  }

  /**
   * Draw all debug overlays
   */
  private draw(layout: ComputedLayout): void {
    // Draw screen bounds
    this.drawBounds(
      layout.screen,
      0xff0000, // Red
      'Screen Bounds',
      2
    )

    // Draw safe area insets
    this.drawSafeArea(layout.screen, layout.safeArea)

    // Draw grid bounds
    this.drawBounds(
      layout.grid.bounds,
      0x00ff00, // Green
      'Grid',
      2
    )

    // Draw HUD bounds
    this.drawBounds(
      layout.hud.bounds,
      0x0000ff, // Blue
      'HUD',
      2
    )

    // Draw Legend bounds
    this.drawBounds(
      layout.legend.bounds,
      0xffff00, // Yellow
      'Legend',
      2
    )

    // Draw pause button position
    this.drawPoint(
      layout.pauseButton.x,
      layout.pauseButton.y,
      0xff00ff, // Magenta
      'Pause Button'
    )

    // Draw layout mode label
    const modeLabel = this.scene.add.text(
      10,
      10,
      `Layout Mode: ${layout.mode}`,
      {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#ffffff',
        backgroundColor: '#000000',
        padding: { x: 8, y: 4 }
      }
    )
    modeLabel.setDepth(10001)
    this.textLabels.push(modeLabel)
  }

  /**
   * Draw bounding box with label
   */
  private drawBounds(
    bounds: { x: number; y: number; width: number; height: number },
    color: number,
    label: string,
    lineWidth: number = 2
  ): void {
    this.graphics.lineStyle(lineWidth, color, 0.8)
    this.graphics.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height)

    // Add label at top-left of bounds
    const text = this.scene.add.text(
      bounds.x + 5,
      bounds.y + 5,
      label,
      {
        fontFamily: 'Arial',
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: '#000000',
        padding: { x: 4, y: 2 }
      }
    )
    text.setDepth(10001)
    this.textLabels.push(text)
  }

  /**
   * Draw safe area insets
   */
  private drawSafeArea(
    screen: { x: number; y: number; width: number; height: number },
    safeArea: { top: number; right: number; bottom: number; left: number }
  ): void {
    this.graphics.lineStyle(2, 0xff8800, 0.6) // Orange

    // Top inset
    if (safeArea.top > 0) {
      this.graphics.strokeRect(0, 0, screen.width, safeArea.top)
      const text = this.scene.add.text(
        screen.width / 2,
        safeArea.top / 2,
        `Safe Top: ${safeArea.top}px`,
        {
          fontFamily: 'Arial',
          fontSize: '10px',
          color: '#ffffff',
          backgroundColor: '#ff8800',
          padding: { x: 4, y: 2 }
        }
      )
      text.setOrigin(0.5)
      text.setDepth(10001)
      this.textLabels.push(text)
    }

    // Right inset
    if (safeArea.right > 0) {
      this.graphics.strokeRect(
        screen.width - safeArea.right,
        0,
        safeArea.right,
        screen.height
      )
    }

    // Bottom inset
    if (safeArea.bottom > 0) {
      this.graphics.strokeRect(
        0,
        screen.height - safeArea.bottom,
        screen.width,
        safeArea.bottom
      )
    }

    // Left inset
    if (safeArea.left > 0) {
      this.graphics.strokeRect(0, 0, safeArea.left, screen.height)
    }
  }

  /**
   * Draw a point marker
   */
  private drawPoint(x: number, y: number, color: number, label: string): void {
    this.graphics.fillStyle(color, 0.8)
    this.graphics.fillCircle(x, y, 5)

    this.graphics.lineStyle(2, color, 0.8)
    this.graphics.strokeCircle(x, y, 10)

    const text = this.scene.add.text(x + 15, y, label, {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#ffffff',
      backgroundColor: '#000000',
      padding: { x: 4, y: 2 }
    })
    text.setDepth(10001)
    this.textLabels.push(text)
  }

  /**
   * Clear all debug graphics
   */
  private clear(): void {
    this.graphics.clear()
    this.textLabels.forEach(text => text.destroy())
    this.textLabels = []
  }

  /**
   * Toggle visibility
   */
  public toggle(): void {
    this.isVisible = !this.isVisible
    if (!this.isVisible) {
      this.clear()
    }
    console.log(`Debug Overlay: ${this.isVisible ? 'ON' : 'OFF'}`)
  }

  /**
   * Set visibility
   */
  public setVisible(visible: boolean): void {
    this.isVisible = visible
    if (!this.isVisible) {
      this.clear()
    }
  }

  /**
   * Get visibility state
   */
  public getVisible(): boolean {
    return this.isVisible
  }

  /**
   * Destroy the debug overlay
   */
  public destroy(): void {
    this.clear()
    this.graphics.destroy()
  }
}
